import 'core-js/stable';
import 'regenerator-runtime/runtime';

// import './misc/redirectToBaseURL';

import './style/index.scss';

import * as Sentry from '@sentry/react';

import CollectContextProvider from './components/collect/CollectContextProvider';
import GlobalErrorScreen from './components/screens/GlobalErrorScreen/GlobalErrorScreen';
import UserInterface from './components/UserInterface/UserInterface';
import ErrorBoundary from './components/validate/ErrorBoundary';
import config from './configs/config';
import handleLogout from './misc/logout';
import { setupHistory } from './misc/setupHistory';

// Enable Sentry for production
if (config.sentryId) {
    console.log('Initializing sentry');
    Sentry.init({
        dsn: config.sentryId,
        release: 'app.networkapp.eu@' + __VERSION__,
        ignoreErrors: ['Non-Error exception captured', 'Non-Error promise rejection captured'],
    });
} else {
    console.warn('Skipping sentry setup');
}

import _ from 'lodash';
import React from 'react';
import { createRoot } from 'react-dom/client';

import { Provider } from 'react-redux';

import axios from 'axios';
import storage from 'local-storage';
import moment from 'moment';
import api from 'networkapp-api-connector';
import { restoreStoreState, saveStoreState } from './data/persist/store-state';
import { enablePushNotifications } from './misc/push-notifications';

// Give range powers to moment
import { extendMoment } from 'moment-range';
if (!moment.range) extendMoment(moment);

// api requests timeout
axios.defaults.timeout = 20000;

// Usefull when debugging
global._ = _;

// Setup api
api.setBaseURL(config.apiURL);
api.setClientId(config.clientId);
api.setSocketURL(config.socketURL);

// Check API connection
import './misc/api-connection-status.js';

// Render root component
function startUI() {
    const root = createRoot(document.getElementById('networkapp'));
    root.render(
        <Provider store={app.store}>
            <ErrorBoundary altComponent={<GlobalErrorScreen />}>
                <CollectContextProvider>
                    <UserInterface />
                </CollectContextProvider>
            </ErrorBoundary>
        </Provider>
    );
    app.onShutdown(() => {
        root.unmount();
    });
}

export const app = function () {
    console.log('\n--- Welcome to app.networkapp.eu! ---');
    console.log('Version: ', __VERSION__);
    console.log('\n\n');

    // Setup history
    setupHistory();

    // Restore saved session if possible
    restoreStoreState();

    // Sync moment locale with state
    let unobserveMomentLocale = app.store.observe(
        function (state) {
            return state.i18n.locale;
        },
        function (locale) {
            console.log('Switch moment locale to: ', locale);
            moment.locale(locale);
        }
    );
    app.onShutdown(unobserveMomentLocale);

    // Save session auth token
    let unobserveSession = app.store.observe(
        function (state) {
            return state.session;
        },
        function (session) {
            if (session.user && session.user.id && session.authToken) {
                storage.set('token', session.authToken);
                storage.set('userId', session.user.id);
            }
        }
    );
    app.onShutdown(unobserveSession);

    // Save prefill data
    let unobservePrefillData = app.store.observe(
        function (state) {
            return state.session.prefill;
        },
        function (prefill) {
            global.sessionStorage.setItem('prefill', prefill ? JSON.stringify(prefill) : null);
        }
    );
    app.onShutdown(unobservePrefillData);

    // Set auth tokens
    let unobserveAuthTokens = app.store.observe(function (state) {
        return state.session.authToken;
    }, api.setAuthToken);
    app.onShutdown(unobserveAuthTokens);

    if (!app.store.getState().session.authToken) {
        const restoreUserSession = () => {
            // Restore previous user session
            let savedSession;
            try {
                savedSession = {
                    userId: storage.get('userId'),
                    token: storage.get('token'),
                };
            } catch (e) {
                console.warn(e);
            }
            if (savedSession && savedSession.userId && savedSession.token) {
                app.store.dispatch(app.actions.ui.busy());
                app.api.session
                    .restoreSession(savedSession.userId, savedSession.token)
                    .then(function () {
                        console.log('Session restored');
                    })
                    .catch(function (error) {
                        console.log('Failed to restore session');
                        if (error.response?.data) {
                            // server answered but call failed
                            // forget about saved token
                            handleLogout();
                        } else {
                            console.error(error);
                        }
                    })
                    .finally(() => {
                        app.store.dispatch(app.actions.ui.free());
                    });
            }
        };

        let unobserve = app.store.observe(
            (state) => state.session.hasAPIConnection,
            (hasAPIConnection) => {
                if (hasAPIConnection) {
                    unobserve();
                    restoreUserSession();
                }
            }
        );
    }

    var prefillData = global.sessionStorage.getItem('prefill');
    if (prefillData) {
        app.store.dispatch(app.actions.session.prefill(JSON.parse(prefillData)));
    }

    startUI();

    // Check window dimensions
    const checkWindowDimensions = _.debounce(() => app.store.dispatch(app.actions.ui.isMobile(window.innerWidth <= 700)), 500);
    window.addEventListener('resize', checkWindowDimensions);
    checkWindowDimensions();

    if (window.parent && window.parent !== window) {
        // notify parent window if ready and inside iframe
        console.log('Notify parent window');
        window.parent.postMessage('webapp-ready', location.origin);
    } else {
        // Enable notifications
        enablePushNotifications();
    }
};
global.app = app;

import chatAPI from './api/chat';
import collectAPI from './api/collect';
import directoryAPI from './api/directory';
import eventsAPI from './api/events';
import formsAPI from './api/forms';
import meetingsAPI from './api/meetings';
import miscAPI from './api/misc';
import notificationsAPI from './api/notifications';
import participantsAPI from './api/participants';
import questionsAPI from './api/questions';
import sessionAPI from './api/session';
import usersAPI from './api/users';
import votingAPI from './api/voting';
import programAPI from './api/program';
import leadsAPI from './api/leads.js';

import storeActions from './data/actions';
import store from './data/store';
import './data/updates';
import shutdown from './misc/app-graceful-shutdown.js';
import { demoMode } from './misc/demoMode.js';

Object.assign(
    app,
    {
        api: {
            session: sessionAPI,
            events: eventsAPI,
            users: usersAPI,
            meetings: meetingsAPI,
            questions: questionsAPI,
            notifications: notificationsAPI,
            chat: chatAPI,
            forms: formsAPI,
            voting: votingAPI,
            misc: miscAPI,
            collect: collectAPI,
            participants: participantsAPI,
            directory: directoryAPI,
            program: programAPI,
            leads: leadsAPI,
        },
        actions: storeActions,
        store: store,
        demoMode,
    },
    shutdown
);

// Load default languages
app.store.dispatch(app.actions.i18n.loadDefaultLanguage());

// Upload data in the background
import './workers/collectUploader';
import './workers/formUploader';

// Persist data on localStorage
import initializeFormPersistence from './data/persist/forms';
_.defer(initializeFormPersistence);

// receive messages from iframes
import './misc/receiveMessages';

// reload when the session changes in another tab
import './misc/session-triggers';

window.reload = function () {
    saveStoreState();
    location.reload();
};

// update body classes
import './misc/update-body-classes';

// force reload app if updated

import './misc/auto-app-updater.js';

// Load app
window.addEventListener('load', app);
